/**
 * Label Templates Shared Schemas
 *
 * Zod schemas for label layout templates used by both frontend and backend.
 * Defines canvas sizes, component types, and validation rules.
 */
import { z } from 'zod';
// ============================================================================
// CANVAS PRESETS
// ============================================================================
export const CANVAS_PRESETS = {
    'compact-32x57': { width: 57, height: 32, name: 'Compact (32x57mm)' },
    'standard-4x3': { width: 102, height: 76, name: 'Standard (4x3")' },
    'shipping-4x6': { width: 102, height: 152, name: 'Shipping (4x6")' },
};
// ============================================================================
// CANVAS SIZE SCHEMA
// ============================================================================
export const CanvasSizeSchema = z.object({
    width: z.number().min(20).max(200), // mm
    height: z.number().min(20).max(200), // mm
    name: z.string().min(1).max(50).regex(/^[a-zA-Z0-9\s\-()x."]+$/, 'Name can only contain letters, numbers, spaces, hyphens, parentheses, x, periods, and quotes'),
});
// ============================================================================
// COMPONENT TYPE SCHEMA
// ============================================================================
// 14 component types - includes binLabel, itemCount, productType, orderNumberBarcode and printArea
export const ComponentTypeSchema = z.enum([
    'qr',
    'barcode',
    'orderNumberBarcode', // CODE128 barcode encoding the order number (BLU-311)
    'orderNumber',
    'sizeColor',
    'productInfo',
    'itemCount', // "Item 2 of 4" - separate from productInfo
    'binLabel', // "-> BIN-042" - separate from productInfo
    'productType', // Shopify product type (e.g., "T-Shirt", "Hoodie")
    'checkboxes',
    'dtgIndicator',
    'ptBadge',
    'redoBadge',
    'printArea', // Phase 104 (BLU-1450, D-09): where each print goes on a manual order's garment ("FRONT LEFT CHEST")
]);
// ============================================================================
// LABEL TYPE SCHEMA
// ============================================================================
export const LabelTypeSchema = z.enum(['POD', 'P&P', 'BOTH']);
// ============================================================================
// COMPONENT AVAILABILITY BY LABEL TYPE
// ============================================================================
/**
 * Defines which label types each component is available for.
 *
 * Rationale:
 * - binLabel: Bins are assigned at DIFFERENT times depending on workflow:
 *   - P&P orders: Bins assigned at label print time
 *   - POD+P&P orders: Bin already assigned (due to P&P items)
 *   - POD-only orders: Bin assigned at CATCHER STATION (AFTER printing)
 *   So binLabel should NOT be available for POD-only templates.
 *
 * - dtgIndicator: Only relevant for POD (print-on-demand) workflows.
 *   P&P items don't go through DTG printing.
 *
 * - ptBadge (pretreat): Only relevant for POD workflows.
 *   Pretreatment is applied before DTG printing on dark garments.
 *
 * - printArea (Phase 104, D-09): where each print goes ("FRONT LEFT CHEST").
 *   Only printed garments have one, so POD and BOTH only, like dtgIndicator.
 */
export const COMPONENT_AVAILABILITY = {
    qr: ['POD', 'P&P', 'BOTH'],
    barcode: ['POD', 'P&P', 'BOTH'],
    orderNumberBarcode: ['POD', 'P&P', 'BOTH'],
    orderNumber: ['POD', 'P&P', 'BOTH'],
    sizeColor: ['POD', 'P&P', 'BOTH'],
    productInfo: ['POD', 'P&P', 'BOTH'],
    itemCount: ['POD', 'P&P', 'BOTH'],
    binLabel: ['P&P', 'BOTH'], // NOT available for POD-only (bin assigned after print)
    productType: ['POD', 'P&P', 'BOTH'], // Shopify product type (e.g., "T-Shirt", "Hoodie")
    checkboxes: ['POD', 'P&P', 'BOTH'],
    dtgIndicator: ['POD', 'BOTH'], // NOT available for P&P (no DTG printing)
    ptBadge: ['POD', 'BOTH'], // NOT available for P&P (no pretreatment needed)
    redoBadge: ['POD', 'P&P', 'BOTH'],
    printArea: ['POD', 'BOTH'], // NOT available for P&P (nothing is printed)
};
/**
 * Check if a component type is available for a given label type.
 */
export function isComponentAvailableForLabelType(componentType, labelType) {
    return COMPONENT_AVAILABILITY[componentType].includes(labelType);
}
// ============================================================================
// COMPONENT CONFIG SCHEMA
// ============================================================================
export const ComponentConfigSchema = z.object({
    id: z.string().min(1).max(50).regex(/^[a-zA-Z0-9_-]+$/, 'ID must be alphanumeric with underscores/hyphens only'),
    type: ComponentTypeSchema,
    position: z.object({
        x: z.number().min(-2).max(250), // Allow slight overflow for edge positioning (tightened from -5)
        y: z.number().min(-2).max(250),
    }),
    size: z.object({
        width: z.number().min(1).max(200),
        height: z.number().min(1).max(200),
    }),
    visible: z.boolean(),
    locked: z.boolean().optional().default(false),
    zIndex: z.number().int().min(0).max(100).optional().default(1),
});
// ============================================================================
// LABEL TEMPLATE SCHEMA
// ============================================================================
export const LabelTemplateSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    name: z.string().min(1).max(100),
    canvasSize: CanvasSizeSchema,
    components: z.array(ComponentConfigSchema).min(1).max(20),
    labelType: LabelTypeSchema,
    isDefault: z.boolean(),
    isBuiltIn: z.boolean(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// ============================================================================
// VALIDATION HELPERS
// ============================================================================
/**
 * Check if components include at least one visible QR code.
 * Required for ALL labels (used for order tracking at catcher/packing stations).
 */
function hasVisibleQrCode(components) {
    return components.some(c => c.type === 'qr' && c.visible);
}
/**
 * Check if any component is visible that shouldn't be for the given label type.
 * Returns the first invalid component type found, or null if all valid.
 */
function findInvalidComponentForLabelType(components, labelType) {
    for (const comp of components) {
        if (comp.visible && !isComponentAvailableForLabelType(comp.type, labelType)) {
            return comp.type;
        }
    }
    return null;
}
/**
 * Get human-readable display name for a component type.
 * Used in validation error messages.
 */
const COMPONENT_DISPLAY_NAMES = {
    qr: 'QR Code',
    barcode: 'SKU Barcode',
    orderNumberBarcode: 'Order Barcode',
    orderNumber: 'Order Number',
    sizeColor: 'Size/Color',
    productInfo: 'Product Info',
    itemCount: 'Item Count',
    binLabel: 'Bin Label',
    productType: 'Product Type',
    checkboxes: 'F/B Checkboxes',
    dtgIndicator: 'DTG Badge',
    ptBadge: 'PT Badge',
    redoBadge: 'Redo Badge',
    printArea: 'Print Area',
};
function getComponentDisplayName(type) {
    return COMPONENT_DISPLAY_NAMES[type];
}
// ============================================================================
// INPUT SCHEMAS FOR MUTATIONS
// ============================================================================
export const CreateTemplateInputSchema = z.object({
    name: z.string().min(1).max(100),
    canvasSize: CanvasSizeSchema,
    components: z.array(ComponentConfigSchema).min(1).max(20),
    labelType: LabelTypeSchema.optional().default('BOTH'),
    isDefault: z.boolean().optional().default(false),
}).refine((data) => {
    // ALL labels require at least one visible QR code for order tracking
    return hasVisibleQrCode(data.components);
}, {
    message: 'QR code is required for all labels (used for order tracking)',
    path: ['components'],
}).refine((data) => {
    // Ensure no visible components that aren't available for the label type
    return findInvalidComponentForLabelType(data.components, data.labelType) === null;
}, (data) => {
    const invalidType = findInvalidComponentForLabelType(data.components, data.labelType);
    const componentName = invalidType ? getComponentDisplayName(invalidType) : 'Component';
    return {
        message: `${componentName} is not available for ${data.labelType} label type`,
        path: ['components'],
    };
});
export const UpdateTemplateInputSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100).optional(),
    canvasSize: CanvasSizeSchema.optional(),
    components: z.array(ComponentConfigSchema).min(1).max(20).optional(),
    labelType: LabelTypeSchema.optional(),
    isDefault: z.boolean().optional(),
}).refine((data) => {
    // When components are provided, validate QR requirement
    // Note: When only labelType is updated, validation happens in the handler
    if (data.components) {
        return hasVisibleQrCode(data.components);
    }
    return true;
}, {
    message: 'QR code is required for all labels (used for order tracking)',
    path: ['components'],
}).refine((data) => {
    // When both components and labelType are provided, validate component availability
    // Note: When only one is updated, validation happens in the handler
    if (data.components && data.labelType) {
        return findInvalidComponentForLabelType(data.components, data.labelType) === null;
    }
    return true;
}, (data) => {
    const invalidType = data.components && data.labelType
        ? findInvalidComponentForLabelType(data.components, data.labelType)
        : null;
    const componentName = invalidType ? getComponentDisplayName(invalidType) : 'Component';
    return {
        message: `${componentName} is not available for ${data.labelType} label type`,
        path: ['components'],
    };
});
// ============================================================================
// QUERY SCHEMAS
// ============================================================================
export const GetDefaultTemplateInputSchema = z.object({
    canvasWidth: z.number(),
    canvasHeight: z.number(),
    labelType: LabelTypeSchema.optional().default('BOTH'),
});
// ============================================================================
// VALIDATION INPUT SCHEMA
// ============================================================================
export const ValidateTemplateInputSchema = z.object({
    components: z.array(ComponentConfigSchema).min(1).max(20),
    canvasSize: CanvasSizeSchema,
    labelType: LabelTypeSchema,
});
// ============================================================================
// COMPONENT CONSTRAINTS (used by validateLabelLayout)
// ============================================================================
/**
 * Constraints per component type - shared between frontend and backend.
 * Minimum sizes ensure readability and scannability.
 */
export const COMPONENT_CONSTRAINTS = {
    qr: {
        minWidth: 15,
        minHeight: 15,
        aspectRatio: 1,
        required: true, // Always required for order tracking
    },
    barcode: {
        minWidth: 30,
        minHeight: 6,
    },
    orderNumberBarcode: {
        minWidth: 25,
        minHeight: 6,
    },
    orderNumber: { minHeight: 4 },
    sizeColor: { minHeight: 4 },
    productInfo: { minHeight: 8 },
    itemCount: { minHeight: 4 },
    binLabel: { minHeight: 4 },
    productType: { minHeight: 4 },
    checkboxes: { minWidth: 10, minHeight: 8 },
    dtgIndicator: { minWidth: 8, minHeight: 4 },
    ptBadge: { minWidth: 6, minHeight: 4 },
    redoBadge: { minWidth: 10, minHeight: 4 },
    // Two short uppercase lines ("FRONT LEFT CHEST" / "BACK FULL BACK").
    printArea: { minWidth: 20, minHeight: 6 },
};
/** Tolerance in mm for bounds checking (consistent across frontend/backend) */
const BOUNDS_TOLERANCE_MM = 1;
/** Quiet zone required for scannable codes (QR and barcode) */
const QUIET_ZONE_MM = 2;
/**
 * Validate a label layout for correctness, scannability, and printing.
 * This is the single source of truth for validation used by both frontend and backend.
 *
 * Checks:
 * 1. QR code required (all label types)
 * 2. Minimum sizes for scannable components
 * 3. Quiet zones for QR and barcode (2mm from edges)
 * 4. No overlap between QR and barcode
 * 5. Components within canvas bounds (1mm tolerance)
 * 6. Component availability for label type
 *
 * @param components - Array of component configurations
 * @param canvasSize - Canvas dimensions in mm
 * @param labelType - Label type (POD, P&P, BOTH)
 * @returns Validation result with errors and warnings
 */
export function validateLabelLayout(components, canvasSize, labelType) {
    const errors = [];
    const warnings = [];
    // Find visible components
    const visibleComponents = components.filter((c) => c.visible);
    const componentsByType = new Map(visibleComponents.map((c) => [c.type, c]));
    // 1. QR code required
    const qr = componentsByType.get('qr');
    if (!qr) {
        errors.push({
            code: 'QR_REQUIRED',
            message: 'QR code is required for all labels',
            type: 'error',
        });
    }
    else {
        // QR minimum size check
        if (qr.size.width < 15 || qr.size.height < 15) {
            errors.push({
                code: 'QR_MIN_SIZE',
                message: 'QR code must be at least 15mm for reliable scanning',
                componentId: qr.id,
                type: 'error',
            });
        }
        else if (qr.size.width < 18 || qr.size.height < 18) {
            // Recommended size warning
            warnings.push({
                code: 'QR_RECOMMENDED_SIZE',
                message: 'QR code should be at least 18mm for optimal scanning',
                componentId: qr.id,
                type: 'warning',
            });
        }
        // QR quiet zone check
        const qrRight = qr.position.x + qr.size.width;
        const qrBottom = qr.position.y + qr.size.height;
        if (qr.position.x < QUIET_ZONE_MM ||
            qr.position.y < QUIET_ZONE_MM ||
            qrRight > canvasSize.width - QUIET_ZONE_MM ||
            qrBottom > canvasSize.height - QUIET_ZONE_MM) {
            errors.push({
                code: 'QR_QUIET_ZONE',
                message: 'QR code must be at least 2mm from label edges (quiet zone for scanning)',
                componentId: qr.id,
                type: 'error',
            });
        }
    }
    // 2. SKU Barcode checks (if visible)
    const barcode = componentsByType.get('barcode');
    if (barcode) {
        if (barcode.size.width < 30) {
            warnings.push({
                code: 'BARCODE_MIN_WIDTH',
                message: 'SKU Barcode width below 30mm may reduce scan reliability',
                componentId: barcode.id,
                type: 'warning',
            });
        }
        if (barcode.size.height < 6) {
            errors.push({
                code: 'BARCODE_MIN_HEIGHT',
                message: 'SKU Barcode height must be at least 6mm for scanning',
                componentId: barcode.id,
                type: 'error',
            });
        }
        const barcodeRight = barcode.position.x + barcode.size.width;
        const barcodeBottom = barcode.position.y + barcode.size.height;
        if (barcode.position.x < QUIET_ZONE_MM ||
            barcode.position.y < QUIET_ZONE_MM ||
            barcodeRight > canvasSize.width - QUIET_ZONE_MM ||
            barcodeBottom > canvasSize.height - QUIET_ZONE_MM) {
            errors.push({
                code: 'BARCODE_QUIET_ZONE',
                message: 'SKU Barcode must be at least 2mm from label edges (quiet zone for scanning)',
                componentId: barcode.id,
                type: 'error',
            });
        }
    }
    // 2b. Order Number Barcode checks (if visible)
    const orderBarcode = componentsByType.get('orderNumberBarcode');
    if (orderBarcode) {
        if (orderBarcode.size.width < 25) {
            warnings.push({
                code: 'ORDER_BARCODE_MIN_WIDTH',
                message: 'Order Barcode width below 25mm may reduce scan reliability',
                componentId: orderBarcode.id,
                type: 'warning',
            });
        }
        if (orderBarcode.size.height < 6) {
            errors.push({
                code: 'ORDER_BARCODE_MIN_HEIGHT',
                message: 'Order Barcode height must be at least 6mm for scanning',
                componentId: orderBarcode.id,
                type: 'error',
            });
        }
        const obRight = orderBarcode.position.x + orderBarcode.size.width;
        const obBottom = orderBarcode.position.y + orderBarcode.size.height;
        if (orderBarcode.position.x < QUIET_ZONE_MM ||
            orderBarcode.position.y < QUIET_ZONE_MM ||
            obRight > canvasSize.width - QUIET_ZONE_MM ||
            obBottom > canvasSize.height - QUIET_ZONE_MM) {
            errors.push({
                code: 'ORDER_BARCODE_QUIET_ZONE',
                message: 'Order Barcode must be at least 2mm from label edges (quiet zone for scanning)',
                componentId: orderBarcode.id,
                type: 'error',
            });
        }
    }
    // 3. Pairwise overlap checks for all scannable components
    const scannableComponents = [qr, barcode, orderBarcode].filter((c) => c !== undefined);
    const SCANNABLE_OVERLAP_CODES = {
        'qr|barcode': { code: 'QR_BARCODE_OVERLAP', message: 'QR code and SKU Barcode cannot overlap (causes scanning failures)' },
        'qr|orderNumberBarcode': { code: 'QR_ORDER_BARCODE_OVERLAP', message: 'QR code and Order Barcode cannot overlap (causes scanning failures)' },
        'barcode|orderNumberBarcode': { code: 'BARCODE_ORDER_BARCODE_OVERLAP', message: 'SKU Barcode and Order Barcode cannot overlap (causes scanning failures)' },
    };
    for (let i = 0; i < scannableComponents.length; i++) {
        for (let j = i + 1; j < scannableComponents.length; j++) {
            const a = scannableComponents[i];
            const b = scannableComponents[j];
            const aRight = a.position.x + a.size.width;
            const aBottom = a.position.y + a.size.height;
            const bRight = b.position.x + b.size.width;
            const bBottom = b.position.y + b.size.height;
            const overlapsX = a.position.x < bRight && aRight > b.position.x;
            const overlapsY = a.position.y < bBottom && aBottom > b.position.y;
            if (overlapsX && overlapsY) {
                const key = `${a.type}|${b.type}`;
                const overlapInfo = SCANNABLE_OVERLAP_CODES[key] || {
                    code: 'SCANNABLE_OVERLAP',
                    message: `${getComponentDisplayName(a.type)} and ${getComponentDisplayName(b.type)} cannot overlap (causes scanning failures)`,
                };
                errors.push({
                    code: overlapInfo.code,
                    message: overlapInfo.message,
                    componentId: a.id,
                    type: 'error',
                });
            }
        }
    }
    // 4. Components within canvas bounds (with 1mm tolerance)
    for (const comp of visibleComponents) {
        const rightEdge = comp.position.x + comp.size.width;
        const bottomEdge = comp.position.y + comp.size.height;
        // Check left/top edges (strict - no negative positions)
        if (comp.position.x < -BOUNDS_TOLERANCE_MM || comp.position.y < -BOUNDS_TOLERANCE_MM) {
            errors.push({
                code: 'OUT_OF_BOUNDS_TOP_LEFT',
                message: `${getComponentDisplayName(comp.type)} extends outside canvas (top/left)`,
                componentId: comp.id,
                type: 'error',
            });
        }
        // Check right/bottom edges (with tolerance)
        if (rightEdge > canvasSize.width + BOUNDS_TOLERANCE_MM) {
            errors.push({
                code: 'OUT_OF_BOUNDS_RIGHT',
                message: `${getComponentDisplayName(comp.type)} extends beyond right edge of canvas`,
                componentId: comp.id,
                type: 'error',
            });
        }
        if (bottomEdge > canvasSize.height + BOUNDS_TOLERANCE_MM) {
            errors.push({
                code: 'OUT_OF_BOUNDS_BOTTOM',
                message: `${getComponentDisplayName(comp.type)} extends beyond bottom edge of canvas`,
                componentId: comp.id,
                type: 'error',
            });
        }
    }
    // 5. Component availability for label type
    for (const comp of visibleComponents) {
        if (!isComponentAvailableForLabelType(comp.type, labelType)) {
            errors.push({
                code: 'COMPONENT_NOT_AVAILABLE',
                message: `${getComponentDisplayName(comp.type)} is not available for ${labelType} label type`,
                componentId: comp.id,
                type: 'error',
            });
        }
    }
    // 6. Label-type specific suggestions
    if (labelType === 'POD' || labelType === 'BOTH') {
        if (!componentsByType.get('dtgIndicator')) {
            warnings.push({
                code: 'DTG_SUGGESTED',
                message: 'Consider adding DTG indicator for POD workflows',
                type: 'warning',
            });
        }
    }
    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}
